import React, { useState, useMemo } from 'react';
import { plusIcon, searchIcon } from '../../utils/images';
import { TagCard } from '../TagCard';
import { TagResponse } from '../../types/Product';
import './TagsList.scss';

interface TagsListProps {
  tags?: TagResponse[];
  onUpdate?: (id: number, name: string) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
  onCreate?: (name: string) => Promise<void>;
}

export const TagsList: React.FC<TagsListProps> = ({ 
  tags, 
  onUpdate,
  onDelete,
  onCreate
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [newTagName, setNewTagName] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const filteredTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) return [];
    if (!searchQuery) return tags;
    
    const query = searchQuery.toLowerCase();
    return tags.filter(tag => 
      tag.name.toLowerCase().includes(query) ||
      tag.id.toString().includes(query)
    );
  }, [tags, searchQuery]);

  const handleCreateTag = async () => {
    if (!onCreate || newTagName.trim() === '') return;

    try {
      setIsCreating(true);
      await onCreate(newTagName.trim());
      setNewTagName('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setNewTagName('');
    setShowCreateForm(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateTag();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  return (
    <div className="tagsList">
      <div className="tagsList__title">
        <label className="tagsList__label">
          <p className="tagsList__label--text">Tags</p>
        </label>
        <button
          onClick={() => setShowCreateForm(true)}
          className="tagsList__add-button"
          disabled={showCreateForm}
        >
          <img 
            className="tagsList__add-button-icon tagsList__add-button-icon--left"
            src={plusIcon} 
            alt="Add"
          />
          <span className="tagsList__add-button-text">Add Tag</span>
        </button>
      </div>

      <div className="tagsList__actions">
        <div className="tagsList__filters">
          <div className="tagsList__search">
            <img className="tagsList__search--icon" src={searchIcon} alt="Search" />
            <input
              type="text"
              placeholder="Search by name or ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tagsList__search--input"
            />
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="tagsList__createForm">
          <div className="tagsList__createInputWrapper">
            <input
              type="text"
              placeholder="Enter tag name..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyPress={handleKeyPress}
              className="tagsList__createInput"
              autoFocus
              disabled={isCreating}
            />
            <div className="tagsList__createActions">
              <button
                onClick={handleCreateTag}
                className="tagsList__createButton"
                disabled={isCreating || newTagName.trim() === ''}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={handleCancelCreate}
                className="tagsList__cancelButton"
                disabled={isCreating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tagsList__list">
        {filteredTags.length > 0 ? (
          filteredTags.map(tag => (
            <TagCard
              key={tag.id}
              tag={tag}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))
        ) : (
          <p className="tagsList__no-results">
            {searchQuery 
              ? 'No tags found. Try adjusting your search criteria.' 
              : 'No tags found. Create your first tag to get started.'
            }
          </p>
        )}
      </div>
    </div>
  );
};